import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import ResizablePanel from "../../components/common/ResizablePanel";
import JournalPanel from "../addressbook/panels/JournalPanel";
import ChatPanel from "../addressbook/panels/ChatPanel";

/*
 * Standalone, GLOBAL journal. NOT tied to a contact or property.
 *
 * Two panes, composed from the same primitives the other two-pane pages use
 * (ResizablePanel, as in PageToDo / PageAddressbook):
 *   - Left: the shared per-contact <JournalPanel> in its contact-free "global"
 *     mode (so all note/reminder actions + styling stay in parity).
 *   - Right: the shared AI <ChatPanel> — the SAME component the contact
 *     Addressbook uses — running in its contact-free "global" mode. There is
 *     exactly ONE chat implementation in the app; this page reuses it rather
 *     than duplicating any chat shell / WebLLM / voice-input logic.
 *
 * Global scope reuses the existing journal collection (no new top-level
 * fields):
 *   - A synthetic ref points at the user: ref { collection: "global",
 *     id: <authorId> }, plus data.scope: "global".
 *   - The active domain is recorded SILENTLY in data.domain for scoping /
 *     provenance only — it is never shown in the UI.
 *   - Every write is stamped with data.globalJournalProvenance.
 *
 * The chat reads context from THIS same global journal (notes + reminders,
 * domain-filtered) and its tool-calls (add_note, create_reminder) write back
 * into it with the same silent scope/domain/provenance stamping. When the chat
 * writes, we bump a refreshKey so the list pane reloads.
 */

const GLOBAL_REF_COLLECTION = "global";
const PROVENANCE_SOURCE = "webapp:PageJournal";
const CHAT_PROVENANCE_SOURCE = "webapp:PageJournalChat";

export default function PageJournal() {
  const authorId = useSelector((state) => state.auth.userId);
  const user = useSelector((state) => state.auth.user);
  const userDomain = String(user?.domain || "").trim();
  // Kept for silent data-layer scoping/provenance only — never rendered.
  const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";

  const refId = useMemo(() => String(authorId || ""), [authorId]);
  const [refreshKey, setRefreshKey] = useState(0);

  // The chat writes notes/reminders into the same global journal — reload the
  // list pane so they appear immediately.
  const handleChatAction = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <Box sx={{ display: "flex", flexDirection: "row", height: "100%", minHeight: 0, minWidth: 0, overflow: "hidden", gap: 1, p: 1 }}>
      {/* Journal list pane */}
      <ResizablePanel edge="right" defaultWidth={720} minWidth={360} maxWidth={900}>
        <JournalPanel
          authorId={authorId}
          refId={refId}
          refCollection={GLOBAL_REF_COLLECTION}
          scope="global"
          domain={activeDomain}
          provenanceKey="globalJournalProvenance"
          provenanceSource={PROVENANCE_SOURCE}
          footerLeftLabel={null}
          hideToolbar
          refreshKey={refreshKey}
          emptyMessage="Sign in to view your journal."
        />
      </ResizablePanel>

      {/* Chat pane (reuses the shared ChatPanel in global mode) */}
      <ResizablePanel edge="right" defaultWidth={480} minWidth={320} maxWidth={700} fillRemaining>
        <ChatPanel
          authorId={authorId}
          scope="global"
          domain={activeDomain}
          refId={refId}
          refCollection={GLOBAL_REF_COLLECTION}
          provenanceKey="globalJournalProvenance"
          provenanceSource={CHAT_PROVENANCE_SOURCE}
          onActionComplete={handleChatAction}
        />
      </ResizablePanel>
    </Box>
  );
}
